/**
 * Session Sync Module
 *
 * Provides cross-tab session synchronization and session validation on app load.
 *
 * Features:
 * - Listens to `storage` events to detect auth state changes in other tabs
 * - Synchronizes login/logout across all open browser tabs
 * - Validates stored tokens on app initialization
 */

import { useAuthStore, AUTH_STORAGE_KEY } from '../stores/authStore';
import { decryptValue } from './storage';

/**
 * Initializes cross-tab session synchronization.
 *
 * Listens to the `storage` event which fires when localStorage is modified
 * by ANOTHER tab (same origin). This allows us to:
 * - Automatically log out all tabs when one tab logs out
 * - Update auth state when another tab logs in
 *
 * @returns A cleanup function to remove the event listener
 */
export function initSessionSync(): () => void {
  const handleStorageChange = (event: StorageEvent) => {
    // Only react to changes on our auth storage key
    if (event.key !== AUTH_STORAGE_KEY) return;

    const store = useAuthStore.getState();

    // If the auth key was removed (logout in another tab)
    if (event.newValue === null) {
      // Only update if we're currently authenticated
      if (store.isAuthenticated) {
        // Directly set state without triggering server-side logout
        // (the other tab already handled that)
        useAuthStore.setState({
          user: null,
          tokens: null,
          isAuthenticated: false,
          isLoading: false,
          error: null,
        });
      }
      return;
    }

    // If the auth key was updated (login/token refresh in another tab)
    if (event.newValue) {
      try {
        // Decrypt and parse the new state
        const decrypted = decryptValue(event.newValue);
        const parsed = JSON.parse(decrypted);
        const newState = parsed?.state;

        if (newState) {
          const { user, tokens, isAuthenticated } = newState;

          // Sync the state from the other tab
          if (isAuthenticated && user && tokens) {
            useAuthStore.setState({
              user,
              tokens,
              isAuthenticated: true,
              isLoading: false,
              error: null,
            });
          } else if (!isAuthenticated) {
            useAuthStore.setState({
              user: null,
              tokens: null,
              isAuthenticated: false,
              isLoading: false,
              error: null,
            });
          }
        }
      } catch {
        // If we can't parse the new value, ignore it
        // (corrupted data, etc.)
      }
    }
  };

  window.addEventListener('storage', handleStorageChange);

  return () => {
    window.removeEventListener('storage', handleStorageChange);
  };
}

/**
 * Validates the current session on app initialization.
 *
 * Checks if we have stored tokens and validates them by attempting
 * a token refresh if the access token might be close to expiry.
 * If validation fails, clears the auth state.
 *
 * This runs once on app load after Zustand rehydration.
 */
export async function validateSession(): Promise<void> {
  const { tokens, isAuthenticated, setLoading } = useAuthStore.getState();

  // If not authenticated, nothing to validate
  if (!isAuthenticated || !tokens?.accessToken) {
    setLoading(false);
    return;
  }

  try {
    // Decode the access token to check expiry (without verifying signature)
    const payload = parseJwtPayload(tokens.accessToken);

    if (!payload || !payload.exp) {
      // Invalid token format — clear auth state
      useAuthStore.getState().logout();
      return;
    }

    const nowInSeconds = Math.floor(Date.now() / 1000);
    const expSeconds = Number(payload.exp);
    const timeUntilExpiry = expSeconds - nowInSeconds;

    // If the access token is expired or expires within 60 seconds,
    // attempt a proactive refresh
    if (timeUntilExpiry < 60 && tokens.refreshToken) {
      try {
        const { authService } = await import('../services/authService');
        const result = await authService.refreshToken({
          refreshToken: tokens.refreshToken,
        });

        if (result?.tokens?.accessToken) {
          useAuthStore.getState().setTokens({
            accessToken: result.tokens.accessToken,
            refreshToken: result.tokens.refreshToken || null,
          });
        } else {
          // Refresh returned unexpected format
          useAuthStore.getState().logout();
        }
      } catch {
        // Refresh failed — clear auth state
        useAuthStore.getState().logout();
      }
    }

    // If access token is still valid (> 60s remaining), session is good
    setLoading(false);
  } catch {
    // Token parsing failed — clear auth state
    useAuthStore.getState().logout();
  }
}

/**
 * Parse the payload from a JWT token without verifying the signature.
 * Used client-side only for inspecting token claims (exp, userId, etc.).
 *
 * @returns The decoded payload or null if parsing fails
 */
function parseJwtPayload(token: string): Record<string, unknown> | null {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return null;

    const payload = parts[1];
    // Handle base64url encoding
    const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );

    return JSON.parse(jsonPayload);
  } catch {
    return null;
  }
}